import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { FABRICA } from "../data/fabrica";
import type { ChapterData } from "../data/content-types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CAPÍTULO 2 — "La máquina 07". Nave industrial: fondo, suelo y decorado propios. */
export class FabricaScene extends WalkChapter {
  protected chapter: ChapterData = FABRICA;
  protected grid = { w: 15, h: 11 };
  protected spawn = { x: 7, y: 9 };
  protected worldSigns = [
    { id: "w_elec", x: 1.4, y: 0.3 },
    { id: "m_auditiva", x: 5.5, y: 0.3 },
    { id: "w_atrapamiento", x: 9.5, y: 0.3 },
    { id: "f_extintor", x: 13.5, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("fabrica");
  }

  // suelo de nave: hormigón industrial con parches de aceite/tierra
  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 12 === 0 ? "dirt" : "concrete";
  }

  // fondo: interior de nave con siluetas industriales y chimeneas
  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x6f6a60);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0x8f897c, 0x8f897c, 0x6f6a60, 0x6f6a60, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.6 + over);
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.42;
    // naves con techo en diente de sierra
    for (const [x, w, ht] of [[40, 190, 74], [300, 230, 96], [700, 150, 58], [900, 250, 104], [1170, 150, 82]] as [number, number, number][]) {
      sk.fillStyle(0x7d786c, 0.92); sk.fillRect(x, base - ht, w, ht);
      sk.fillStyle(0x8a857a, 0.9);
      for (let i = 0; i * 40 < w; i++) sk.fillTriangle(x + i * 40, base - ht, x + i * 40 + 20, base - ht - 14, x + i * 40 + 40, base - ht);
    }
    // chimeneas + humo
    const stack = (x: number) => {
      sk.fillStyle(0x6a655b, 1); sk.fillRect(x, base - 150, 14, 150);
      sk.fillStyle(0xb23a2a, 0.8); sk.fillRect(x, base - 150, 14, 8);
      // humo animado: bocanadas que suben, se dispersan y se desvanecen
      const topX = x + 7, topY = base - 154;
      for (let i = 0; i < 3; i++) {
        const puff = this.add.circle(topX, topY, 9, 0xcfc7b6, 0.4).setScrollFactor(0.25).setDepth(-18901);
        this.tweens.add({ targets: puff, y: topY - 72, x: topX + 20, scale: 2.5, alpha: 0, duration: 3400, delay: i * 1100, repeat: -1, repeatDelay: 200, ease: "Sine.easeOut" });
      }
    };
    stack(250); stack(880);
    // paredes de la nave (chapa) para cerrar el interior
    this.buildWalls({ left: 0x5c665c, right: 0x717d70, height: 165, base: 0x474f47, texture: "metal" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };
    // carril de circulación pintado (dos líneas amarillas) por delante de la máquina
    const g = this.add.graphics().setDepth(-9500);
    g.lineStyle(4, 0xe6c200, 0.85);
    for (const yy of [4.7, 5.7]) {
      const a = this.iso(2, yy), b = this.iso(this.grid.w - 2, yy);
      g.beginPath(); g.moveTo(a.sx, a.sy + TILE_H / 2); g.lineTo(b.sx, b.sy + TILE_H / 2); g.strokePath();
    }
    // manchas de aceite
    for (const [x, y] of [[6, 6], [8.4, 6.6], [9, 4.6], [5, 7]] as [number, number][]) {
      const p = this.iso(x, y), cx = p.sx, cy = p.sy + TILE_H / 2;
      g.fillStyle(0x2b2620, 0.28); g.fillEllipse(cx, cy, 26, 12);
      g.fillStyle(0x2b2620, 0.18); g.fillEllipse(cx + 8, cy + 4, 10, 6);
    }
    this.decor.push(g);
    // props de nave (materiales, tubos, bidones, máquina extra, seguridad)
    put(P.pipes(this), 1.8, 2.2);
    put(P.pipes(this), 12.6, 8.6);
    put(P.barrel(this, 0x4a7ba0), 2.4, 3.4);
    put(P.barrel(this, 0xc4632a), 3.2, 3.8);
    put(P.crateStack(this), 12.6, 2.4);
    put(P.pallet(this), 11.4, 9.2);
    put(P.sacks(this), 13.2, 4.6);
    put(P.machine(this, 0x5a6570), 2.6, 6.6);
    put(P.extinguisher(this), 1.4, 5.2);
    put(P.cone(this), 6, 6.4);
    put(P.cone(this), 9, 4.2);
    put(P.fence(this), 4.6, 9.6);
    put(P.fence(this), 6.1, 9.7);

    // --- más props de nave (densidad) ---
    put(P.shelving(this), 13.6, 2.0);
    put(P.shelving(this), 1.6, 8.6);
    put(P.gasrack(this), 13.6, 6.6);
    put(P.toolbox(this), 3.0, 5.4);
    put(P.toolbox(this, 0x2f6b8f), 8.4, 8.4);
    put(P.hosecoil(this), 5.4, 2.9);
    put(P.hosecoil(this, 0x2a2f36), 10.4, 8.0);
    put(P.barrel(this, 0x3a7a3a), 2.0, 4.4);
    put(P.pallet(this), 4.2, 2.6);
    put(P.pipes(this), 7.6, 1.6);
    put(P.cone(this), 7.2, 6.9);
    put(P.crateStack(this), 1.8, 6.4);

    // --- detalle de suelo: rejillas de desagüe + tornillería dispersa ---
    const gd = this.add.graphics().setDepth(-9400);
    for (const [dx, dy] of [[4, 3], [10, 7]] as [number, number][]) {
      const p = this.iso(dx, dy), cy = p.sy + TILE_H / 2;
      gd.fillStyle(0x3a3f47, 0.5); gd.fillRect(p.sx - 11, cy - 4, 22, 9);
      gd.lineStyle(1, 0x14141a, 0.6);
      for (let i = -8; i <= 8; i += 3) { gd.beginPath(); gd.moveTo(p.sx + i, cy - 4); gd.lineTo(p.sx + i, cy + 5); gd.strokePath(); }
    }
    gd.fillStyle(0x4a4f57, 0.6);
    for (const [bx, by] of [[7.4, 5.4], [8.6, 7.2], [3.6, 6.8]] as [number, number][]) { const p = this.iso(bx, by); for (let k = 0; k < 4; k++) gd.fillCircle(p.sx + (k * 5 - 8), p.sy + TILE_H / 2 + (k % 2) * 4, 1.6); }
    this.decor.push(gd);

    // --- estructura aérea: carril de puente grúa + vigas transversales ---
    const oh = this.add.graphics().setDepth(48000);
    oh.lineStyle(6, 0x5a5f68, 1);
    const railA = this.iso(1, 2), railB = this.iso(13, 2);
    oh.beginPath(); oh.moveTo(railA.sx, railA.sy - 152); oh.lineTo(railB.sx, railB.sy - 152); oh.strokePath();
    oh.lineStyle(4, 0x4a4f57, 0.9);
    for (const vx of [3, 6, 9, 12]) { const a = this.iso(vx, 0.5), b = this.iso(vx, 4); oh.beginPath(); oh.moveTo(a.sx, a.sy - 150); oh.lineTo(b.sx, b.sy - 160); oh.strokePath(); }
    // carro del puente grúa con gancho
    const cart = this.iso(7, 2); oh.fillStyle(0xd8a800, 1); oh.fillRect(cart.sx - 12, cart.sy - 160, 24, 12);
    oh.lineStyle(2, 0x2a2a30, 1); oh.beginPath(); oh.moveTo(cart.sx, cart.sy - 148); oh.lineTo(cart.sx, cart.sy - 120); oh.strokePath();
    this.decor.push(oh);

    // --- luminarias colgantes + focos de luz en el suelo (pool de dos capas) ---
    for (const [lx, ly] of [[3.5, 3], [7.5, 3], [11.5, 3], [5.5, 6.5], [9.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly), cx = p.sx, cy = p.sy + TILE_H / 2;
      const pg = this.add.graphics().setDepth(-9300);
      pg.fillStyle(0xffe6a0, 0.15); pg.fillEllipse(cx, cy, 82, 40);
      pg.fillStyle(0xfff4c8, 0.14); pg.fillEllipse(cx, cy, 46, 22);
      this.decor.push(pg); this.addPulse(pg, 0.13, 0.005, 0.86);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(cx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // --- instalaciones en la pared trasera: bandeja de cables + tubo ---
    const wf = this.add.graphics().setDepth(-11900);
    const wa = this.iso(-0.5, -0.5), wb = this.iso(this.grid.w - 0.5, -0.5), wdl = this.iso(-0.5, this.grid.h - 0.5);
    wf.lineStyle(4, 0x3a3f47, 0.9);
    wf.beginPath(); wf.moveTo(wa.sx, wa.sy - 122); wf.lineTo(wb.sx, wb.sy - 122); wf.moveTo(wa.sx, wa.sy - 122); wf.lineTo(wdl.sx, wdl.sy - 122); wf.strokePath();
    wf.lineStyle(3, 0x6a6f76, 0.85);
    wf.beginPath(); wf.moveTo(wa.sx, wa.sy - 96); wf.lineTo(wb.sx, wb.sy - 96); wf.strokePath();
    this.decor.push(wf);

    // chispas de soldadura en la máquina lateral (2.6,6.6)
    this.addRisers(2.6, 6.6, { count: 8, color: 0xffb050, alpha: 0.95, r: 1.6, rise: 20, sp: 0.024, grow: 0.2, dyBase: -20, depth: 45000 });
    this.addRisers(2.6, 6.6, { count: 4, color: 0xffffff, alpha: 0.9, r: 1.1, rise: 24, sp: 0.03, grow: 0.1, dx: 5, dyBase: -22, depth: 45000 });

    if (this.state.chapter("fabrica").done) this.onResolved();
  }

  protected onResolved(): void {
    // resguardo repuesto (verde) sobre la máquina + candado en el cuadro
    const m = this.iso(7, 4);
    this.add.rectangle(m.sx, m.sy - 30, 40, 26, 0x2f8f57, 0.55).setStrokeStyle(2, 0x1e6b3f).setDepth(70);
    const c = this.iso(11, 4);
    this.add.rectangle(c.sx, c.sy - 30, 16, 14, 0x2f8f57).setStrokeStyle(2, 0x1e6b3f).setDepth(70);
  }
}
